import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../context/PermissionsContext';
import { getAllOrgs, setOrgDisabled, type Org } from '../services/org.service';
import { Button, Spinner, toastError, toastSuccess } from '../components/core';

interface OrgWithMemberCount extends Org {
  memberCount: number;
}

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const { appUser } = usePermissions();
  const [orgs, setOrgs] = useState<OrgWithMemberCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toggling, setToggling] = useState<Record<string, boolean>>({});

  // Check if user is super admin
  useEffect(() => {
    if (appUser && !appUser.isSuperAdmin) {
      navigate('/', { replace: true });
    }
  }, [appUser, navigate]);

  // Load all orgs
  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const fetchedOrgs = await getAllOrgs();
        setOrgs(fetchedOrgs);
      } catch {
        setError('Failed to load organizations');
        toastError('Failed to load organizations');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handleToggleDisabled(orgId: string, isDisabled: boolean) {
    setToggling(prev => ({ ...prev, [orgId]: true }));
    try {
      await setOrgDisabled(orgId, isDisabled);
      setOrgs(prev =>
        prev.map(org =>
          org.id === orgId ? { ...org, isDisabled } : org
        )
      );
      toastSuccess(isDisabled ? 'Organization disabled' : 'Organization re-enabled');
    } catch {
      toastError('Failed to update organization');
    } finally {
      setToggling(prev => { const next = { ...prev }; delete next[orgId]; return next; });
    }
  }

  if (!appUser?.isSuperAdmin) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-green-50">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-50">
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        <div className="mb-6">
          <h1 className="font-display text-2xl font-semibold text-gray-900">
            All Organizations
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Platform-wide view. Disable or inspect any organization.
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        {/* Desktop Table */}
        <div className="hidden overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm sm:block">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-6 py-3 text-start font-body text-xs font-semibold uppercase text-gray-600">
                  Organization
                </th>
                <th className="px-6 py-3 text-start font-body text-xs font-semibold uppercase text-gray-600">
                  Plan
                </th>
                <th className="px-6 py-3 text-start font-body text-xs font-semibold uppercase text-gray-600">
                  Members
                </th>
                <th className="px-6 py-3 text-start font-body text-xs font-semibold uppercase text-gray-600">
                  Status
                </th>
                <th className="px-6 py-3 text-end font-body text-xs font-semibold uppercase text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {orgs.map((org, index) => (
                <tr
                  key={org.id}
                  className={`${org.isDisabled ? 'opacity-60' : ''} ${index !== orgs.length - 1 ? 'border-b border-gray-200' : ''}`}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded bg-green-100 text-xs font-bold text-green-800">
                        {org.name
                          .split(' ')
                          .slice(0, 2)
                          .map(w => w[0])
                          .join('')
                          .toUpperCase()}
                      </div>
                      <div>
                        <div className="font-body text-sm font-medium text-gray-900">
                          {org.name}
                        </div>
                        <div className="font-body text-xs text-gray-500">
                          org/{org.slug}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 font-body text-sm text-gray-700">
                    <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                      {org.plan.charAt(0).toUpperCase() + org.plan.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 font-body text-sm font-semibold text-gray-700">
                    {org.memberCount}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        org.isDisabled
                          ? 'bg-gray-100 text-gray-700'
                          : 'bg-green-100 text-green-700'
                      }`}
                    >
                      <span>{org.isDisabled ? '○' : '●'}</span>
                      {org.isDisabled ? 'Disabled' : 'Active'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-end">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate(`/admin/orgs/${org.slug}`)}
                      >
                        View
                      </Button>
                      <button
                        type="button"
                        onClick={() => handleToggleDisabled(org.id, !org.isDisabled)}
                        disabled={toggling[org.id] ?? false}
                        className={`rounded px-3 py-1.5 font-body text-xs font-medium transition-colors ${
                          org.isDisabled
                            ? 'bg-green-600 text-white hover:bg-green-700 disabled:opacity-50'
                            : 'bg-red-600 text-white hover:bg-red-700 disabled:opacity-50'
                        }`}
                      >
                        {toggling[org.id] ? (org.isDisabled ? 'Re-enabling...' : 'Disabling...') : org.isDisabled ? 'Re-enable' : 'Disable'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="space-y-3 sm:hidden">
          {orgs.map(org => (
            <div
              key={org.id}
              className={`rounded-lg border border-gray-200 bg-white p-4 ${org.isDisabled ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded bg-green-100 text-xs font-bold text-green-800">
                  {org.name
                    .split(' ')
                    .slice(0, 2)
                    .map(w => w[0])
                    .join('')
                    .toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="font-body font-medium text-gray-900">
                    {org.name}
                  </div>
                  <div className="font-body text-xs text-gray-500">
                    org/{org.slug}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                        org.isDisabled
                          ? 'bg-gray-100 text-gray-700'
                          : 'bg-green-100 text-green-700'
                      }`}
                    >
                      <span>{org.isDisabled ? '○' : '●'}</span>
                      {org.isDisabled ? 'Disabled' : 'Active'}
                    </span>
                    <span className="font-body text-xs text-gray-500">
                      {org.memberCount} member{org.memberCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate(`/admin/orgs/${org.slug}`)}
                  className="flex-1"
                >
                  View
                </Button>
                <button
                  type="button"
                  onClick={() => handleToggleDisabled(org.id, !org.isDisabled)}
                  disabled={toggling[org.id] ?? false}
                  className={`flex-1 rounded px-3 py-1.5 font-body text-xs font-medium transition-colors ${
                    org.isDisabled
                      ? 'bg-green-600 text-white hover:bg-green-700 disabled:opacity-50'
                      : 'bg-red-600 text-white hover:bg-red-700 disabled:opacity-50'
                  }`}
                >
                  {toggling[org.id] ? (org.isDisabled ? 'Re-enabling...' : 'Disabling...') : org.isDisabled ? 'Re-enable' : 'Disable'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
